/**
 * Who may author, edit and delete news articles.
 *
 * Mirrors the backend gate in `core/views/news_views.py::is_news_manager`:
 * Helix platform users, plus admins of the CUSTOMER company that owns the
 * install. Authorship used to be Helix-only, so a tenant's own admins could
 * not publish announcements to their suppliers.
 *
 * Lives here rather than inline because the rule is read in two places — the
 * News list (Create / Edit / Delete controls) and the News form (which renders
 * nothing without it). Inline copies drifted apart once already.
 */
import type { User } from './api/types/common';

/** The shape both call sites actually pass — `useAuth().user` may be null. */
type NewsUser = (Pick<User, 'is_staff' | 'is_superuser' | 'is_company_admin'> & {
  company_type?: string;
}) | null | undefined;

/**
 * @param user - the authenticated user, or null/undefined when not loaded yet
 * @returns true when this user may create, edit and delete news articles
 */
export function canManageNews(user: NewsUser): boolean {
  if (!user) return false;
  if (user.is_staff || user.is_superuser) return true;
  if (user.company_type === 'helix') return true;
  // A customer admin runs their own portal's announcements. `is_company_admin`
  // is the user API's view of `profile.role.is_admin_role`.
  return user.company_type === 'customer' && Boolean(user.is_company_admin);
}
